import { useMemo, useRef, useState } from 'react';
import { cn, PaletteUtils } from '@shared/utils';
import { useIndicatorPosition } from './useIndicatorPosition';

const fixPx = (value: number) => (value === 0 ? '0' : value + 'px');

type TabOption<T extends string | number | undefined> = {
  value: T;
  label: React.ReactNode;
};
interface TabsProps<T extends string | number | undefined> {
  className?: string;
  listClassName?: string;
  labelClassName?: string;
  activeLabelClassName?: string;
  loading?: boolean;
  hideIndicator?: boolean;
  options?: TabOption<T>[];
  value?: T;
  onChange?: (value: T) => void;
}

export const Tabs = <T extends string | number | undefined>(props: TabsProps<T>) => {
  const [indexState, setIndex] = useState(0);
  const ref = useRef<HTMLUListElement>(null);

  const index = useMemo(() => {
    if (props.value && props.options) return props.options.findIndex((item) => item.value === props.value);
    else return indexState;
  }, [props.value, props.options, indexState]);
  const { scale, shift, ready } = useIndicatorPosition({ index, ref: ref });

  const indicatorStyles = useMemo(() => ({ transform: `translateX(${fixPx(shift)}) scaleX(${scale})` }), [scale, shift]);
  return (
    <div className={cn(PaletteUtils.isVcfcPalette && props.hideIndicator !== true && 'pb-3', props.className)}>
      <ul ref={ref} className={cn('relative flex select-none items-stretch gap-8', props.listClassName)}>
        {props.options?.map((item, idx) => (
          <li
            key={idx}
            className={cn(
              'relative cursor-pointer py-1 transition-all duration-200',
              [
                'before:absolute before:bottom-0 before:left-0 before:z-20 before:h-[2px] before:w-full before:transition-colors before:duration-200 hover:before:bg-primary',
                PaletteUtils.isVcfcPalette && 'before:bottom-[calc(0px-10px)] dark:hover:before:bg-white',
                props.hideIndicator && 'before:hidden',
              ],
              PaletteUtils.isInsigniaPalette && ['opacity-50'],
              PaletteUtils.isVcfcPalette && ['text-base text-soft-black dark:text-white/50', index === idx && 'dark:text-white'],
              idx === index && 'cursor-default font-semibold opacity-100',
              props.labelClassName,
              props.value === item.value && props.activeLabelClassName,
            )}
            onClick={() => (props.onChange ? props.onChange(item.value) : setIndex(idx))}
          >
            {item.label}
          </li>
        ))}
        <span
          className={cn(
            'absolute bottom-0 h-[2px] w-full origin-left rounded bg-primary',
            PaletteUtils.isVcfcPalette && 'bottom-[-10px] z-20 dark:bg-white',
            ready && 'transition-transform duration-300',
            props.hideIndicator && 'hidden',
          )}
          style={indicatorStyles}
        />
        {PaletteUtils.isVcfcPalette && props.hideIndicator !== true && (
          <span className='absolute bottom-[-10px] z-10 h-[2px] w-full origin-left transform rounded border-none bg-gray-black dark:bg-dark-ice-white' />
        )}
      </ul>
    </div>
  );
};
